import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { createBaseTileLayer } from '../../core/base-map';
import { MapObjectColors } from '../../core/map-object-colors';
import { placePinIcon } from '../../core/place-pin';
import { routeStopPinIcon } from '../../core/route-stop-pin';
import { hasMapCoordinates } from '../../core/spatial/map-viewport';

export interface PreviewLocation {
  latitude: number | null;
  longitude: number | null;
  title?: string;
  sequence?: number;
  color?: string;
}

/** Read-only location/ordered-stop preview, independent of application map state. */
@Component({
  selector: 'app-location-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './location-preview.component.html',
  styleUrls: ['./location-preview.component.css'],
})
export class LocationPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() latitude?: number | null;
  @Input() longitude?: number | null;
  @Input() color: string = MapObjectColors.slate;
  /** When supplied, replaces the single location and connects stops in this order. */
  @Input() points: readonly PreviewLocation[] | null = null;
  @ViewChild('canvas', { static: true }) private canvas!: ElementRef<HTMLDivElement>;
  tileError = false;
  private map?: L.Map;
  private marker?: L.Marker;
  private markers?: L.LayerGroup;
  private tiles?: L.TileLayer;
  private observer?: ResizeObserver;
  private ready = false;

  constructor(private readonly zone: NgZone) {}

  private get locations(): readonly PreviewLocation[] {
    return (this.points ?? [{ latitude: this.latitude ?? null, longitude: this.longitude ?? null }])
      .filter(p => hasMapCoordinates(p.latitude, p.longitude));
  }
  get located(): boolean { return this.locations.length > 0; }
  get incomplete(): boolean { return this.points != null && this.locations.length !== this.points.length; }

  ngAfterViewInit(): void { this.ready = true; this.render(); }
  ngOnChanges(): void { this.render(); }
  ngOnDestroy(): void { this.ready = false; this.clear(); }

  private render(): void {
    if (!this.ready) return;
    this.zone.runOutsideAngular(() => {
      if (!this.located) { this.clear(); return; }
      if (!this.map) {
        this.tileError = false;
        this.map = L.map(this.canvas.nativeElement, {
          zoomControl: this.points !== null, dragging: this.points !== null, scrollWheelZoom: this.points !== null,
          doubleClickZoom: this.points !== null, touchZoom: this.points !== null, boxZoom: false, keyboard: this.points !== null,
        });
        this.map.attributionControl.setPrefix(false);
        this.tiles = createBaseTileLayer();
        this.tiles.on('tileerror', () => this.zone.run(() => { this.tileError = true; }));
        this.tiles.addTo(this.map);
        this.markers = L.layerGroup().addTo(this.map);
        this.observer = new ResizeObserver(() => {
          this.map?.invalidateSize({ animate: false });
          this.frameLocations();
        });
        this.observer.observe(this.canvas.nativeElement);
      }
      this.markers!.clearLayers();
      const locations = this.locations;
      if (this.points && !this.incomplete && locations.length > 1) {
        L.polyline(locations.map(p => [p.latitude!, p.longitude!]), {
          color: this.color, weight: 2, dashArray: '5 6', interactive: false,
        }).addTo(this.markers!);
      }
      locations.forEach((p, index) => {
        const marker = L.marker([p.latitude!, p.longitude!], {
          icon: p.sequence == null ? placePinIcon(p.color ?? this.color) : routeStopPinIcon(p.sequence, p.color ?? this.color),
          keyboard: false, interactive: !!p.title, zIndexOffset: p.sequence == null ? 0 : 1000,
        }).addTo(this.markers!);
        if (p.title) {
          const title = document.createElement('span');
          title.textContent = p.title;
          marker.bindTooltip(title);
        }
        if (index === 0) this.marker = marker;
      });
      this.frameLocations();
    });
  }

  private frameLocations(): void {
    if (!this.map || !this.located) return;
    const points: L.LatLngExpression[] = this.locations.map(p => [p.latitude!, p.longitude!]);
    if (this.points) this.map.fitBounds(L.latLngBounds(points), { padding: [24, 36], maxZoom: 16, animate: false });
    else this.map.setView(points[0], 15, { animate: false });
  }

  private clear(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.tiles?.off();
    this.map?.remove();
    this.map = undefined;
    this.marker = undefined;
    this.markers = undefined;
    this.tiles = undefined;
  }
}
